import { NotificationService } from '../../../App/services/mx-notification-service';
import { I18n } from '../../../i18n/mx-internationalization';
import { ProcessItem } from '../model/process-item';
import { MxBackgroundProcessProvider } from './mx-background-process-provider';
import { MxTrackedProcessProvider } from './mx-tracked-process-provider';

const asyncContract = 'cbd32a6d-cdfc-45c2-8957-0eb09219e5dd';

type AsyncOperationOptions = {
	AsyncOperationContract: string;
	OperationId: string;
	OperationName: string;
	Completed: boolean;
	FinishTime?: Date;
};

const enum ProcessType {
	Background = 'Background',
	Foreground = 'Foreground',
}

type ProcessStorage = Record<ProcessType, Array<ProcessItem>>;

/**
 * @ngdoc service
 * @name mx.shell.BackgroundProcessesService
 * @module mx.shell
 *
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$q" target="_blank">$q</a>
 * @requires {@ref mx.shell.Http mx.shell.Http}
 *
 * @description
 * Provides logic for obtaining and managing current background processes of Application.
 *
 * @property {object[]} trackedProcessesCount - Gets number of tracked background processes on that moment of time.
 * @property {object[]} backgroundProcesses - Gets list of the background processes that moment of time.
 * @property {object[]} foregroundProcesses - Gets list of the foreground processes that moment of time.
 * */
export class MxBackgroundProcessesService {
	static $inject = ['mx.shell.BackgroundProcessProvider', 'mx.shell.TrackedProcessProvider', 'mx.shell.NotificationService', 'mx.internationalization'];
	private _isLoading = false;
	private _reloadOnAdd = false;
	private processStorage: ProcessStorage = {
		Background: [],
		Foreground: [],
	};
	constructor(
		private readonly backgroundProcessProvider: MxBackgroundProcessProvider,
		private readonly trackedProcessProvider: MxTrackedProcessProvider,
		private readonly notificationService: NotificationService,
		private readonly i18n: I18n
	) {}

	public get trackedProcessesCount(): number {
		return this.trackedProcessProvider.count;
	}

	public get backgroundProcesses(): Array<ProcessItem> {
		return this.processStorage[ProcessType.Background];
	}

	public get foregroundProcesses(): Array<ProcessItem> {
		return this.processStorage[ProcessType.Foreground].filter((process) => !process.isExpired);
	}

	public get allProcesses(): Array<ProcessItem> {
		return this.backgroundProcesses.concat(this.foregroundProcesses);
	}

	public get isLoading(): boolean {
		return this._isLoading;
	}

	public set isLoading(value) {
		this._isLoading = Boolean(value);
	}

	public get reloadOnAdd(): boolean {
		return this._reloadOnAdd;
	}

	public set reloadOnAdd(value) {
		this._reloadOnAdd = Boolean(value);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.BackgroundProcessesService#loadBackgroundProcesses
	 *
	 * @description
	 * Loads active background processes from server
	 *
	 * @returns {object} promise
	 */
	public async loadBackgroundProcesses(): Promise<boolean> {
		this._isLoading = true;
		const backgroundProcesses = await this.backgroundProcessProvider.load();
		const runningProcesses = backgroundProcesses.concat(this.foregroundProcesses).filter((process) => process.id && !process.finishTime);
		this.processStorage[ProcessType.Background] = backgroundProcesses;
		this.trackedProcessProvider.sync(runningProcesses.map((process) => process.id));
		this._isLoading = false;
		return Boolean(backgroundProcesses);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.BackgroundProcessesService#addTrackedProcess
	 *
	 * @description
	 * Adds new id of new process to be tracked
	 */
	public addTrackedProcess(obj: AsyncOperationOptions): void {
		if (obj && !obj.Completed && obj.AsyncOperationContract?.toLowerCase() === asyncContract && obj.OperationId) {
			this.trackedProcessProvider.add(obj.OperationId);
			const msg = this.i18n.getFormatted('shell.mx-shell-backgroundProcessesService.operation-started', obj.OperationName);
			this.notificationService.info(msg);
			if (this._reloadOnAdd) {
				this.loadBackgroundProcesses();
			}
		}
	}

	public addForegroundProcess(item: ProcessItem): void {
		const items = this.processStorage[ProcessType.Foreground];
		const index = items.findIndex((process) => process.id === item.id);
		if (index < 0) {
			this.processStorage[ProcessType.Foreground] = items.filter((item) => !item.isExpired).concat([item]);
			this.trackedProcessProvider.add(item.id);
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.BackgroundProcessesService#cancelProcess
	 *
	 * @description
	 * Cancels existing process
	 *
	 * @param {object} item - process info to be deleted
	 */
	public async cancelProcess(item: ProcessItem): Promise<void> {
		const backgroundProcess = this.processStorage[ProcessType.Background].find((process) => process.id === item.id);
		if (backgroundProcess) {
			await this.backgroundProcessProvider.cancelProcess(backgroundProcess);
		} else {
			await this.processStorage[ProcessType.Foreground].find((process) => process.id === item.id)?.cancel();
		}
		this.loadBackgroundProcesses();
	}
}
